/**
 * A file leads back to its message (#1069 Story 7.6, FR79; D1, D3, D6, D7).
 *
 * The pull-until-found loop, as its own hook. The panel asks `request(id)`;
 * if the message is among the loaded pages it is revealed at once; otherwise
 * older pages are pulled through the data layer's OWN `loadOlderMessages`
 * (`useCorrespondence.ts`, the same `fetchNextPage` the scroll-up path uses,
 * owner decision D1: no `?around=` endpoint, the pages stay contiguous), one
 * at a time, until the page holding it lands. Then `reveal` carries the id and
 * a nonce to `MessageList`, which performs the scroll against its own ref.
 *
 * ⚠️ IT IS ITS OWN HOOK, NOT A MEMBER OF `useCorrespondence` (D3). This is UI
 * orchestration over the data layer's `fetchNextPage`, not data-layer work,
 * and Epic 6's surface pin is a contract. The shape is the one the deleted
 * `useCloseOnUndock.ts` had (2026-09-25): refs for the callback and the
 * counters, an effect on primitives.
 *
 * ⚠️ "LOAD UNTIL FOUND" WITH NO PROGRESS GUARD IS AN INFINITE LOOP (C6). The
 * panel suite's `listMessages` stub returns the same page with the same cursor
 * on every call, and nothing on the wire guarantees a cursor advances. Two
 * stops the epic did not name, both pinned:
 *
 *  · NO PROGRESS: a landed page that left the cursor unchanged AND brought no
 *    message id that was not already loaded. ⚠️ Ids, not `messages.length`:
 *    `messagesInReadingOrder` (`correspondenceQueries.ts`) does not dedupe, so
 *    a repeated page GROWS the length while adding nothing a reader could be
 *    taken to. Measured at `5e9fe550e`; the story text said "length" and the
 *    code wins.
 *  · THE CAP: `MAX_JUMP_PAGES`. See its docblock.
 *
 * Plus the end of the thread: `olderMessagesCursor === null` with the message
 * absent. Every exit KEEPS the pages already loaded (D6): the reader asked for
 * history and got some, and "Load earlier messages" still works.
 *
 * ⚠️ THE LANDING IS SIGNALLED BY THE PROMISE, NOT BY `loadingOlder` ALONE.
 * React Query publishes through `notifyManager.schedule` (`setTimeout(0)`),
 * and inside an `act()` or a batched update the `loadingOlder: true` render can
 * be coalesced away entirely -- so an edge on that flag is not a signal a test
 * or a fast stub can be trusted to produce. `loadOlderMessages()` resolves
 * after the fetch has settled and the cache holds the page; the resolution
 * bumps a counter, the re-render reads the observer's CURRENT result
 * (`useSyncExternalStore`'s `getSnapshot` runs during that render), and the
 * effect judges progress against the snapshot it took before the call.
 * `loadingOlder` still matters: a page on the wire that is not ours (the
 * reader pressed "Load earlier") is judged when it lands, never while it
 * flies, and no call is issued while one is in flight.
 *
 * What cancels a pull (D7): another `request` (supersedes: the counter
 * restarts, the old fetch's settlement is ignored), `cancel()` (the Files row
 * collapse, `leaveOpenThread`), and an `openThreadId` change (a full reset).
 * Nothing else -- scrolling or typing is not a change of mind.
 *
 * @see src/components/correspondence/__tests__/useJumpToMessage.test.ts
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Message } from './correspondence.types';

/**
 * The cap on pages pulled for ONE request (D6). 25 × `DEFAULT_PAGE_LIMIT` (20)
 * = 500 = `MAX_THREAD_FILE_LISTING` (`provider.types.ts`): a file the listing
 * can show is, at that bound, in a thread the loop will walk. A message deeper
 * than 500 messages is "unreachable" from the strip; the reader gets the
 * panel's line (D9) and the "Load earlier messages" control still works. That
 * is the measurement D1's `?around=` alternative would need, and it is
 * recorded in `deferred-work.md` as such.
 */
export const MAX_JUMP_PAGES = 25;

export interface JumpReveal {
  messageId: string;
  /**
   * Bumped on every reveal, so the same file chosen twice scrolls twice and a
   * re-render never re-scrolls: `MessageList` keys its effect on this.
   */
  nonce: number;
}

export type JumpFailure = 'unreachable' | null;

export interface UseJumpToMessageInputs {
  openThreadId: string | null;
  /** Reading order, as `useCorrespondence` hands it over. */
  messages: readonly Message[];
  olderMessagesCursor: string | null;
  /**
   * The FIRST page is still on the wire. Before it lands `messages` is empty
   * and the cursor is `null`, which reads exactly like "the beginning reached
   * and the message absent" -- so a press made before the thread has loaded
   * (a cached listing, a slow first page) waits instead of failing (review
   * 2026-09-19).
   */
  loadingMessages: boolean;
  loadingOlder: boolean;
  /**
   * The data layer says the last older-page fetch FAILED (its own bar is
   * already showing "loading earlier messages: ..."). A failed fetch lands no
   * page, which the no-progress stop would otherwise read as "the server
   * repeated itself" and call the message unreachable. When this is set the
   * pull stops quietly: the pages are kept, no line is added, and the next
   * strip press tries again (review 2026-09-19).
   */
  olderPageFailed: boolean;
  loadOlderMessages: () => Promise<void>;
}

export interface JumpToMessage {
  request: (messageId: string) => void;
  cancel: () => void;
  /** Non-null while older pages are being pulled for this message. */
  pendingMessageId: string | null;
  reveal: JumpReveal | null;
  failure: JumpFailure;
}

interface Snapshot {
  ids: Set<string>;
  cursor: string | null;
}

export function useJumpToMessage({
  openThreadId,
  messages,
  olderMessagesCursor,
  loadingMessages,
  loadingOlder,
  olderPageFailed,
  loadOlderMessages,
}: UseJumpToMessageInputs): JumpToMessage {
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  const [reveal, setReveal] = useState<JumpReveal | null>(null);
  const [failure, setFailure] = useState<JumpFailure>(null);
  /** Re-runs the effect when a fetch we issued settles, or a request is made. */
  const [tick, setTick] = useState(0);

  // The callback through a ref, so the panel may pass what it likes without
  // re-arming the effect (the shape named in the header).
  const loadRef = useRef(loadOlderMessages);
  useEffect(() => {
    loadRef.current = loadOlderMessages;
  }, [loadOlderMessages]);

  const nonce = useRef(0);
  const pagesPulled = useRef(0);
  /** A fetch we issued has not settled yet. */
  const inFlight = useRef(false);
  /** What was loaded when the last fetch we issued was started. */
  const snapshot = useRef<Snapshot | null>(null);
  /** Which request an in-flight settlement belongs to; a stale one is ignored. */
  const token = useRef(0);
  /**
   * The thread the pending request was made for. The reset effect's state
   * updates are not visible to the effect below in the SAME commit as a thread
   * switch, and its ref resets are -- so without this the effect would see the
   * old pending id with a clear in-flight flag and issue one more fetch for
   * the new thread. Measured in the hook's own suite (case h).
   */
  const requestedFor = useRef<string | null>(null);
  const alive = useRef(true);
  // Set in the body as well as cleared in the cleanup: under StrictMode's
  // simulated unmount/remount the cleanup runs once without a real unmount,
  // and a ref that is only ever cleared would leave every later settlement
  // ignored (review 2026-09-19).
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const resetPull = useCallback(() => {
    token.current += 1;
    pagesPulled.current = 0;
    inFlight.current = false;
    snapshot.current = null;
  }, []);

  const request = useCallback(
    (messageId: string) => {
      resetPull();
      requestedFor.current = openThreadId;
      setFailure(null);
      setPendingMessageId(messageId);
      // The effect below decides everything, including the immediate reveal,
      // so a present message and an absent one take the same path and the
      // `failure` transition is always visible to the panel's effect.
      setTick(t => t + 1);
    },
    [resetPull, openThreadId],
  );

  const cancel = useCallback(() => {
    resetPull();
    setPendingMessageId(null);
    setReveal(null);
    setFailure(null);
  }, [resetPull]);

  // A thread switch resets everything (D7): the pages, the counter and the
  // request all belonged to the conversation that was open.
  useEffect(() => {
    cancel();
  }, [openThreadId, cancel]);

  useEffect(() => {
    if (pendingMessageId === null) return;
    // A thread switch in this same commit: the reset above has cleared the
    // request, and what is pending belongs to the conversation that was open.
    if (openThreadId !== requestedFor.current) return;

    if (messages.some(m => m.id === pendingMessageId)) {
      nonce.current += 1;
      resetPull();
      setReveal({ messageId: pendingMessageId, nonce: nonce.current });
      setPendingMessageId(null);
      return;
    }

    // The thread itself has not loaded yet: an empty list with a null cursor
    // is not "the beginning reached", it is "nothing has arrived". Wait.
    if (messages.length === 0 && loadingMessages) return;

    // Our own fetch is still on the wire: nothing to judge and nothing to ask.
    if (inFlight.current) return;

    const fail = () => {
      resetPull();
      setPendingMessageId(null);
      setFailure('unreachable');
    };

    if (snapshot.current !== null) {
      // A fetch we issued has settled. If the page is STILL on the wire from
      // the observer's point of view (our call joined somebody else's fetch
      // and returned at once), judge it when it lands, not now.
      if (loadingOlder) return;
      const before = snapshot.current;
      snapshot.current = null;
      const progress =
        olderMessagesCursor !== before.cursor ||
        messages.some(m => !before.ids.has(m.id));
      if (!progress) {
        if (olderPageFailed) {
          // The fetch failed rather than the server repeating itself: the
          // data layer's own bar says so. Stop, keep the pages, add no line.
          resetPull();
          setPendingMessageId(null);
          return;
        }
        fail();
        return;
      }
    }

    if (olderMessagesCursor === null) {
      fail();
      return;
    }
    if (pagesPulled.current >= MAX_JUMP_PAGES) {
      fail();
      return;
    }
    // Somebody else's page is on the wire (the reader pressed "Load earlier"):
    // wait for it; this effect re-runs when it lands.
    if (loadingOlder) return;

    pagesPulled.current += 1;
    snapshot.current = {
      ids: new Set(messages.map(m => m.id)),
      cursor: olderMessagesCursor,
    };
    inFlight.current = true;
    const issued = token.current;
    const settle = () => {
      if (!alive.current || issued !== token.current) return;
      inFlight.current = false;
      setTick(t => t + 1);
    };
    // Both branches settle; nothing is left floating.
    loadRef.current().then(settle, settle);
  }, [
    openThreadId,
    pendingMessageId,
    messages,
    olderMessagesCursor,
    loadingMessages,
    loadingOlder,
    olderPageFailed,
    tick,
    resetPull,
  ]);

  return { request, cancel, pendingMessageId, reveal, failure };
}
